/**
 * Shared utility functions for gift box and personalisation features.
 * Loaded before gift-option-buttons.js, gift-box-add-to-cart.js,
 * cart-gifting-drawer.js, and personalisation-drawer.js.
 *
 * Usage: window.GiftUtils.fetchCart(), window.GiftUtils.log(), etc.
 */
;(function () {
  'use strict'

  var GiftUtils = {}

  // --- Debug logging ---

  window.__GIFTPROPS_DEBUG__ = false

  /**
   * Conditional debug logger for gift box operations.
   * Enable at runtime: window.__GIFTPROPS_DEBUG__ = true
   * @param {string} message - Description of what is being logged
   * @param {*} [payload] - Optional data to inspect
   */
  GiftUtils.log = function (message, payload) {
    if (!window.__GIFTPROPS_DEBUG__) return
    if (payload !== undefined) {
      console.warn('%c[GiftBox Debug] ' + message, 'color:#8A2BE2;font-weight:bold', payload)
    } else {
      console.warn('%c[GiftBox Debug] ' + message, 'color:#8A2BE2;font-weight:bold')
    }
  }

  // --- Cart API ---

  /**
   * Fetch the current cart state from /cart.js
   * @returns {Promise<Object>} Parsed cart JSON
   */
  GiftUtils.fetchCart = async function () {
    var res = await fetch('/cart.js', {
      cache: 'no-store',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
    if (!res.ok) throw new Error('/cart.js returned ' + res.status)
    return res.json()
  }

  /**
   * POST to a Shopify cart endpoint.
   * @param {string} endpoint - One of '/cart/add.js', '/cart/change.js', '/cart/update.js'
   * @param {Object} data - The request body (will be JSON.stringify'd)
   * @returns {Promise<Response>} The raw fetch Response
   */
  GiftUtils.cartPost = async function (endpoint, data) {
    return fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(data),
    })
  }

  // --- Cart item lookup ---

  /**
   * Find an addon item in the cart matching a groupId or parentVariantId.
   * @param {Array} cartItems - Array of cart item objects from /cart.js
   * @param {string|null} groupId - The _groupId property to match
   * @param {string|null} parentVariantId - The _parentPid to match as fallback
   * @param {string} [addonType] - Optional addon type filter ('Gift Box', 'Personalisation')
   * @returns {Object|null} The matching addon cart item, or null
   */
  GiftUtils.findAddon = function (cartItems, groupId, parentVariantId, addonType) {
    var filterAddon = function (item) {
      var props = item.properties || {}
      var isAddon = props._addon === 'true' || props._addon === true
      if (!isAddon) return false
      if (addonType && props._addonType !== addonType) return false
      return true
    }

    // Prefer groupId match (most specific)
    if (groupId) {
      var byGroupId = cartItems.find(function (item) {
        if (!filterAddon(item)) return false
        return item.properties._groupId === String(groupId)
      })
      if (byGroupId) return byGroupId
    }

    // Fallback: parentVariantId (for legacy items without groupId)
    if (parentVariantId) {
      return cartItems.find(function (item) {
        if (!filterAddon(item)) return false
        return item.properties._parentPid === String(parentVariantId)
      }) || null
    }

    return null
  }

  /**
   * Convenience alias for finding Gift Box addons specifically.
   */
  GiftUtils.findGiftBoxAddon = function (cartItems, groupId, parentVariantId) {
    return GiftUtils.findAddon(cartItems, groupId, parentVariantId, 'Gift Box')
  }

  // --- Group ID ---

  /**
   * Generate a unique group ID for linking main products with addon products.
   * @returns {string} A timestamp-based unique ID
   */
  GiftUtils.generateGroupId = function () {
    return Date.now().toString()
  }

  // --- DOM / UI helpers ---

  /**
   * Toggle body scroll lock for drawer overlays.
   * @param {boolean} lock - true to prevent scrolling, false to restore it
   */
  GiftUtils.setBodyOverflowHidden = function (lock) {
    var method = lock ? 'add' : 'remove'
    document.body.classList[method]('overflow-hidden--drawer-open')
    document.documentElement.classList[method]('overflow-hidden--drawer-open')
  }

  /**
   * Toggle the loading/disabled state on the cart items container.
   * @param {boolean} loading - true to show loading state, false to remove it
   * @returns {HTMLElement|null} The container element
   */
  GiftUtils.setCartLoading = function (loading) {
    var container =
      document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems')
    if (container) {
      container.classList[loading ? 'add' : 'remove']('cart__items--disabled')
    }
    return container
  }

  /**
   * Parse an HTML string and extract innerHTML of a given selector.
   * @param {string} html - Raw HTML string
   * @param {string} selector - CSS selector to find within parsed document
   * @returns {string} innerHTML of matched element
   * @throws {Error} if selector is not found in the parsed HTML
   */
  GiftUtils.parseSectionHTML = function (html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html')
    var el = doc.querySelector(selector)
    if (!el) {
      throw new Error('parseSectionHTML: selector not found: ' + selector)
    }
    return el.innerHTML
  }

  /**
   * Trigger the cart drawer/items custom element to refresh its UI.
   * Calls onCartUpdate() on whichever cart element exists in the DOM.
   */
  GiftUtils.refreshCartUI = function () {
    var cartDrawerItems =
      document.querySelector('cart-drawer-items') || document.querySelector('cart-items')
    if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
      cartDrawerItems.onCartUpdate()
    }
  }

  /**
   * Size a Shopify product.js image URL for gift box cards.
   * @param {string} src
   * @param {number} [width]
   * @returns {string}
   */
  GiftUtils.giftBoxImageUrl = function (src, width) {
    width = width || 400
    if (!src) return ''
    var url = src
    if (url.indexOf('//') === 0) url = 'https:' + url
    return url.replace(/(\.\w+)(\?|$)/, '_' + width + 'x$1$2')
  }

  GiftUtils.escapeHtml = function (str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  GiftUtils.syncGiftBoxOptionSelection = function (root) {
    var scope = root || document
    scope.querySelectorAll('.gifting-box-option').forEach(function (option) {
      var input = option.querySelector('.gifting-box-option__input')
      option.classList.toggle(
        'gifting-box-option--selected',
        !!(input && input.checked)
      )
    })
  }

  GiftUtils.updateGiftBoxOptionsNav = function (viewport) {
    if (!viewport) return
    var row = viewport.querySelector('.gifting-box-options__row')
    var nav = viewport.querySelector('.gifting-box-options__nav')
    if (!row || !nav) return
    var canScroll = row.scrollWidth - row.clientWidth > 8
    nav.hidden = !canScroll
  }

  GiftUtils.initGiftBoxOptions = function (root) {
    var scope = root || document
    scope.querySelectorAll('.gifting-box-options__viewport').forEach(function (viewport) {
      var row = viewport.querySelector('.gifting-box-options__row')
      if (!row) return
      GiftUtils.updateGiftBoxOptionsNav(viewport)
      if (row.dataset.giftBoxNavBound === 'true') return
      row.dataset.giftBoxNavBound = 'true'
      row.addEventListener('scroll', function () {
        GiftUtils.updateGiftBoxOptionsNav(viewport)
      })
    })
    GiftUtils.syncGiftBoxOptionSelection(scope)
  }

  document.addEventListener('click', function (event) {
    var nav = event.target.closest('.gifting-box-options__nav')
    if (!nav) return
    event.preventDefault()
    var viewport = nav.closest('.gifting-box-options__viewport')
    var row = viewport && viewport.querySelector('.gifting-box-options__row')
    if (!row) return
    var card = row.querySelector('.gifting-box-option')
    var amount = card ? card.getBoundingClientRect().width + 8 : 208
    var maxScroll = row.scrollWidth - row.clientWidth
    if (row.scrollLeft >= maxScroll - 4) {
      row.scrollTo({ left: 0, behavior: 'smooth' })
    } else {
      row.scrollBy({ left: amount, behavior: 'smooth' })
    }
  })

  document.addEventListener('change', function (event) {
    if (!event.target.classList.contains('gifting-box-option__input')) return
    var options = event.target.closest('.gifting-box-options')
    GiftUtils.syncGiftBoxOptionSelection(options || document)
  })

  document.addEventListener('DOMContentLoaded', function () {
    GiftUtils.initGiftBoxOptions(document)
  })

  window.GiftUtils = GiftUtils
})()
